import { Group, Stack, Text, Tooltip } from '@mantine/core';
import { formatBytes } from '@/utils/misc.ts';
import { useViewDataset } from '@/hooks/useViewDataset.ts';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import useRetrieveDataset from '@/hooks/api/datasets/useRetrieveDataset.ts';
import { useHover } from '@mantine/hooks';
import { HiRefresh, HiX } from 'react-icons/hi';
import DatasetIcon from '@/components/DatasetIcon.tsx';
import { ReactNode } from 'react';

type props = {
  name: string;
  size?: number;
  onDelete: () => void;
  onReplace?: () => void;
  datasetId?: string;
  py?: string | number;
  metadata?: ReactNode;
  actions?: ReactNode;
  removeLabel?: string;
};

const FileItem = ({
  name,
  size,
  onDelete,
  onReplace,
  datasetId,
  py = '8px',
  metadata,
  actions,
  removeLabel,
}: props) => {
  const { setDatasetModalId } = useViewDataset();
  const { data: dataset } = useRetrieveDataset(datasetId);
  const allowClick = !!datasetId;

  const { hovered, ref } = useHover();

  return (
    <Stack
      ref={ref}
      styles={{
        root: {
          background: '#F8F8F8',
          border: '1px solid var(--mantine-color-gray-2)',
          borderRadius: '5px',
          paddingLeft: '8px',
          paddingRight: '8px',
          paddingTop: py,
          paddingBottom: py,
          backgroundColor: hovered ? '#EAEAEA' : '#f1f3f5',
        },
      }}
      gap={4}
    >
      <Group justify="space-between" wrap="nowrap" gap={0}>
        <Group
          px="4px"
          gap="xs"
          style={{
            cursor: allowClick ? 'pointer' : 'default',
            overflow: 'hidden',
            flex: 1,
            minWidth: 0,
          }}
          onClick={async () => {
            if (allowClick) {
              await setDatasetModalId(datasetId);
            }
          }}
          wrap="nowrap"
        >
          <DatasetIcon dataset={dataset} size={'20px'} showPlaceholderIcon={!datasetId} />

          <Stack gap="0px" style={{ overflow: 'hidden' }}>
            <Text size="sm" truncate>
              {name}
            </Text>
            {dataset?.processing_error && (
              <Text size="xs" c="red">
                {dataset?.processing_error}
              </Text>
            )}
            {size !== undefined && (
              <Text size="xs" c="dimmed">
                {formatBytes(size, 1)}
              </Text>
            )}
          </Stack>
        </Group>
        <Group gap={0} wrap="nowrap">
          {actions}
          {onReplace && (
            <Tooltip label="Replace data source" openDelay={250}>
              <StyledButton
                variant="subtleIcon"
                size="sm"
                onClick={onReplace}
                aria-label={`Replace ${name}`}
                styles={{
                  root: { backgroundColor: 'transparent', color: 'var(--mantine-color-gray-7)' },
                }}
              >
                <HiRefresh size={16} />
              </StyledButton>
            </Tooltip>
          )}
          <StyledButton
            variant="subtleIcon"
            size="sm"
            onClick={onDelete}
            aria-label={removeLabel ?? `Delete ${name}`}
            styles={{
              root: { backgroundColor: 'transparent', color: 'var(--mantine-color-gray-7)' },
            }}
          >
            <HiX size={16} />
          </StyledButton>
        </Group>
      </Group>
      {metadata != null && (
        <Stack gap={0} px="4px">
          {metadata}
        </Stack>
      )}
    </Stack>
  );
};

export default FileItem;
